import React, { createContext, useContext } from 'react';
import type { PermissionName } from 'c-sdk';

/**
 * UL-7912 -- which kind of campaign the shared schedule components are rendering for.
 *
 * The right rail (ScheduleList, ScheduleListItem) and the rule editor are shared between a direct
 * campaign and a DSP one. Rather than thread a flag through every props layer, the DSP container
 * provides this context, and those components read it.
 *
 * The DEFAULT is the direct page, exactly as it behaves today. A direct campaign never renders a
 * provider, so nothing about it can change by accident.
 */
export type ScheduleVariant = {
    variant: 'direct' | 'dsp';

    /** How many rows the DSP table has ticked -- enables ＋ and － on a schedule. */
    selectedCount: number;

    /** Apply the schedule to the ticked insertion orders. */
    onAttach?: (scheduleId: number) => void | Promise<void>;

    /** Return the ticked insertion orders on this schedule to the Default. */
    onDetach?: (scheduleId: number) => void | Promise<void>;

    /** The permissions ＋ and － need in this variant. */
    attachPermissions?: PermissionName[];
};

const DIRECT: ScheduleVariant = { variant: 'direct', selectedCount: 0 };

const ScheduleVariantContext = createContext<ScheduleVariant>(DIRECT);

export const ScheduleVariantProvider: React.FC<{
    value: ScheduleVariant;
    children: React.ReactNode;
}> = ({ value, children }) => (
    <ScheduleVariantContext.Provider value={value}>{children}</ScheduleVariantContext.Provider>
);

export const useScheduleVariant = (): ScheduleVariant => useContext(ScheduleVariantContext);

/** The DSP rule editor has no share of voice and no slot length -- the DSP reports what it delivered. */
export const useIsDspSchedule = (): boolean => useScheduleVariant().variant === 'dsp';
